import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, Divider, Typography } from "@mui/material";
import { parseISO, subDays } from "date-fns";
import {
  apiForLine,
  isLineReady,
  lineNotReadyReason,
  LAM_LINE_HEADING,
  type LamLineSelection,
  type LamShiftRollsRow,
} from "../../../api/lamLine";
import LineSelector from "../../common/LineSelector";
import CardHeaderRow from "../../common/CardHeaderRow";
import GroupSelector, {
  DEFAULT_GROUPING,
  type LamGrouping,
} from "../../common/GroupSelector";
import TimeframeSelector, {
  DEFAULT_TIMEFRAME,
  timeframeDays,
  type LamTimeframe,
} from "../../common/TimeframeSelector";
import { nowAsNashvilleLocal } from "../../../utils/nashvilleTime";
import { RollsLegend } from "../Lamination2/Lam2RollsChart";
import RollsPanel from "./RollsPanel";
import RollMixPanel from "./RollMixPanel";
import {
  bucketShiftRolls,
  type RollBucketsReport,
} from "../../../utils/rollBuckets";

/**
 * Roll volume and roll quality, in one card, off one set of shifts.
 *
 * The two used to be separate cards with a selector each, which made a pair of
 * questions out of one: whether the mix chart was describing the same line and
 * the same buckets as the volume chart above it was something you had to check
 * rather than something you could see. One card, one line, one grouping, one
 * timeframe, one fetch — and the two charts cannot disagree.
 *
 * Volume first because it is the coarser read: how much came off the line. The
 * mix underneath answers what state it came off in, on the same x axis, so a
 * bad week is visible as both a dip and a drift without moving your eyes off
 * the period.
 */

const REFRESH_MS = 60_000;
/**
 * Shifts to ask for. The API caps this at 100; at the two shifts a day this
 * line runs, that is comfortably past the 90-day timeframe. The window itself
 * is applied below, so a shorter timeframe is a filter, not a smaller fetch.
 */
const SHIFT_LIMIT = 100;

type Props = {
  /**
   * Pin the card to one line and drop its line switch. The overview shows the
   * two lines side by side, so each card there is about one line only.
   */
  line?: LamLineSelection;
  /** Shared y-axis ceiling for the counts chart, from the overview. */
  yMax?: number;
  /**
   * Told this line's buckets, for the overview's shared y-axis. Each line keeps
   * its own x axis: a day this line has no data for is left off, even if the
   * other line ran.
   */
  onBuckets?: (report: RollBucketsReport) => void;
};

const RollAnalyticsCard: React.FC<Props> = ({
  line: pinnedLine,
  yMax,
  onBuckets,
}) => {
  const [chosenLine, setLine] = useState<LamLineSelection>("lam2");
  const line = pinnedLine ?? chosenLine;
  const [group, setGroup] = useState<LamGrouping>(DEFAULT_GROUPING);
  const [timeframe, setTimeframe] = useState<LamTimeframe>(DEFAULT_TIMEFRAME);

  const [rows, setRows] = useState<LamShiftRollsRow[]>([]);
  const [loading, setLoading] = useState(true);

  const unavailable = isLineReady(line) ? null : lineNotReadyReason(line);

  useEffect(() => {
    // Keep the last real charts on screen to blur as the template, and do not
    // ask the API for data we would not draw.
    if (!isLineReady(line)) {
      setLoading(false);
      return;
    }
    const ctrl = new AbortController();
    // Drop the previous line's shifts before fetching, so they are not read as
    // this line's under the new heading.
    setRows([]);
    setLoading(true);

    const load = async (silent = false) => {
      try {
        const data = await apiForLine(line).getShiftRolls({
          limit: SHIFT_LIMIT,
          signal: ctrl.signal,
        });
        setRows(data);
      } catch {
        /* leave the last good data on screen */
      } finally {
        if (!silent) setLoading(false);
      }
    };

    load();
    const id = setInterval(() => load(true), REFRESH_MS);
    return () => {
      clearInterval(id);
      ctrl.abort();
    };
  }, [line]);

  // Shift starts are Nashville wall-clock, so the cutoff is taken in that frame
  // — an hour of UTC drift would move a 06:00 shift across a day boundary.
  const buckets = useMemo(() => {
    const cutoff = subDays(nowAsNashvilleLocal(), timeframeDays(timeframe));
    const windowed = rows.filter((r) => {
      const at = parseISO(r.shift_start);
      return !Number.isNaN(at.getTime()) && at >= cutoff;
    });
    return bucketShiftRolls(windowed, group);
  }, [rows, timeframe, group]);

  // A blurred template is not this line's output, so it must not add days to,
  // or set the scale of, the other line's chart.
  useEffect(() => {
    onBuckets?.({ group, buckets: unavailable ? [] : buckets });
  }, [onBuckets, group, buckets, unavailable]);

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardContent sx={{ flex: 1, p: { xs: 2, md: 2.5 } }}>
        <CardHeaderRow
          titleNoWrap
          title={
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {LAM_LINE_HEADING[line]} — Roll Analytics
            </Typography>
          }
          controls={
            /* One question in three parts: which output, over how long, cut
               how finely. */
            <>
              {!pinnedLine && <LineSelector value={line} onChange={setLine} />}
              <GroupSelector value={group} onChange={setGroup} />
              <TimeframeSelector value={timeframe} onChange={setTimeframe} />
            </>
          }
          aside={<RollsLegend />}
        />

        <RollsPanel
          buckets={buckets}
          loading={loading}
          unavailable={unavailable}
          yMax={yMax}
        />

        <Divider sx={{ my: 2.5 }} />

        <RollMixPanel
          buckets={buckets}
          group={group}
          loading={loading}
          unavailable={unavailable}
        />
      </CardContent>
    </Card>
  );
};

export default RollAnalyticsCard;
